import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { label: 'Forside', to: '/' },
    { label: 'Om Bowtech', to: '/projects' },
    { label: 'Behandlingen', to: '/behandlingen' },
    { label: 'Om Jørn', to: '/om-jorn' },
    { label: 'Priser', to: '/about-us' },
    { label: 'Klienthistorier', to: '/klienthistorier' },
    { label: 'Kontakt', to: '/contact' },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#faf8f5]/95 backdrop-blur-md shadow-sm py-3 border-b border-[#228A47]/10'
          : 'bg-[#faf8f5] py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="group flex flex-col focus:outline-none focus:ring-2 focus:ring-[#228A47] rounded-md p-1 transition-transform active:scale-[0.98]"
          aria-label="Rolskov Bowtech Forside"
        >
          <span className="text-xl sm:text-2xl font-bold tracking-tight text-[#062364] group-hover:text-[#228A47] transition-colors">
            Rolskov Bowtech
          </span>
          <span className="text-xs font-semibold text-[#228A47] tracking-wide">
            Bowtech-behandling i Skårup
          </span>
        </Link>

        {/* Desktop Navigation (Visible >= 1200px as required) */}
        <nav className="hidden min-[1200px]:flex items-center gap-1 xl:gap-2" aria-label="Hovedmenu">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-semibold rounded-lg transition-all duration-200 relative ${
                  isActive
                    ? 'text-[#228A47] bg-[#e8f5ed] shadow-xs'
                    : 'text-[#062364] hover:text-[#228A47] hover:bg-[#f4efe6]/70'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Action Callouts */}
        <div className="hidden min-[1200px]:flex items-center gap-4">
          <a
            href="tel:27117557"
            className="whitespace-nowrap text-sm font-semibold text-[#062364] hover:text-[#228A47] transition-colors flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#f4efe6]/60"
          >
            <i className="fa-regular fa-phone text-[#228A47]" aria-hidden="true"></i>
            <span>27 11 75 57</span>
          </a>

          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-[#228A47] hover:bg-[#1b713a] active:bg-[#155a2d] rounded-xl shadow-sm transition-all duration-200 hover:shadow transform active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#228A47] focus:ring-offset-2"
          >
            Bestil tid
          </Link>
        </div>

        {/* Mobile / Tablet Menu Button (Visible < 1200px) */}
        <div className="flex items-center gap-3 min-[1200px]:hidden">
          <a
            href="tel:27117557"
            className="hidden sm:flex whitespace-nowrap text-xs font-semibold text-[#228A47] bg-[#e8f5ed] px-3 py-1.5 rounded-xl items-center gap-1.5 hover:bg-[#228A47] hover:text-white transition-colors"
          >
            <i className="fa-solid fa-phone" aria-hidden="true"></i>
            <span>27 11 75 57</span>
          </a>

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-[#062364] hover:text-[#228A47] hover:bg-[#f4efe6] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#228A47]"
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'fa-regular fa-xmark' : 'fa-regular fa-bars'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile / Tablet Menu Overlay (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-[#faf8f5] border-b border-[#228A47]/10 shadow-lg px-4 pt-3 pb-6 animate-fade-in">
          <nav className="flex flex-col gap-1.5" aria-label="Mobilmenu">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-4 py-3 text-base font-semibold rounded-lg transition-colors ${
                    isActive
                      ? 'text-[#228A47] bg-[#e8f5ed]'
                      : 'text-[#062364] hover:bg-[#f4efe6]'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-5 pt-4 border-t border-[#228A47]/10 flex flex-col gap-3">
            <a
              href="tel:27117557"
              className="whitespace-nowrap w-full py-3 px-4 text-center text-sm font-semibold text-[#062364] bg-[#f4efe6] hover:bg-[#e8f5ed] rounded-xl flex items-center justify-center gap-2 transition-colors"
            >
              <i className="fa-regular fa-phone text-[#228A47]" aria-hidden="true"></i>
              <span>Ring direkte: 27 11 75 57</span>
            </a>

            <Link
              to="/contact"
              className="w-full py-3 px-4 text-center text-sm font-semibold text-white bg-[#228A47] hover:bg-[#1b713a] rounded-xl shadow-sm transition-colors"
            >
              Bestil tid / Kontakt
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
